(() => {
  "use strict";

  // Public feed playback. Studio keeps its own editing/export lifecycle.
  const selector = ".drop-media-item video";
  const publicMedia = ".drop-media-item video, .drop-media-item audio, .flow-video, .flow-audio";
  const players = new Map();
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  let active = null, frame = 0;
  const canAutoplay = () => document.visibilityState !== "hidden" && !motion.matches && !navigator.connection?.saveData;
  const make = (tag, name) => {
    const node = document.createElement(tag);
    if (name) node.className = name;
    return node;
  };
  const time = value => {
    const seconds = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  };
  const icons = {
    play: "M8 5v14l11-7z", pause: "M8 5v14M16 5v14",
    sound: "M11 5L6 9H3v6h3l5 4zM15 8c3 2 3 6 0 8M18 5c5 4 5 10 0 14",
    muted: "M11 5L6 9H3v6h3l5 4zM16 9l6 6M22 9l-6 6",
    expand: "M9 3H3v6M15 3h6v6M21 15v6h-6M9 21H3v-6",
    captions: "M3 5h18v14H3zM10 9H7v6h3M17 9h-3v6h3",
  };
  const label = (button, name, icon) => {
    button.setAttribute("aria-label", name);
    button.title = name;
    const path = button.querySelector("path");
    if (path) path.setAttribute("d", icons[icon]);
  };
  const button = (name, icon) => {
    const node = make("button", "video-control"); node.type = "button";
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24"); svg.setAttribute("aria-hidden", "true");
    svg.append(document.createElementNS("http://www.w3.org/2000/svg", "path"));
    node.append(svg); label(node, name, icon); return node;
  };
  function visible(player) {
    const { video } = player;
    if (!video.isConnected || document.visibilityState === "hidden" || video.closest("[hidden]")
        || video.closest("[data-gallery-active='false']")) return 0;
    if (document.querySelector("dialog[open]") && !video.closest("dialog[open]")) return 0;
    const fullscreen = document.fullscreenElement;
    const ownsFullscreen = Boolean(fullscreen && (fullscreen === video || fullscreen.contains?.(video)));
    const rect = video.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || rect.right <= 0 || rect.left >= innerWidth) return 0;
    const nav = document.querySelector(".mobile-bottom-nav");
    const bottom = !ownsFullscreen && nav && getComputedStyle(nav).position === "fixed" && nav.getClientRects().length
      ? Math.min(innerHeight, nav.getBoundingClientRect().top) : innerHeight;
    const clip = ownsFullscreen ? null : video.closest(".drop-media-carousel")?.querySelector(".drop-media-gallery")?.getBoundingClientRect();
    const horizontal = Math.max(0, Math.min(rect.right, innerWidth, clip?.right ?? innerWidth)
      - Math.max(rect.left, 0, clip?.left ?? 0)) / rect.width;
    const vertical = Math.max(0, Math.min(rect.bottom, bottom, clip?.bottom ?? bottom)
      - Math.max(rect.top, 0, clip?.top ?? 0)) / Math.min(rect.height, bottom);
    return Math.min(horizontal, vertical);
  }
  function pause(player) {
    player.generation += 1;
    player.requested = false;
    if (player.repeatTimer) clearTimeout(player.repeatTimer);
    player.repeatAt = 0;
    player.clearWait();
    player.video.pause();
    player.sync();
  }
  function release(player) {
    pause(player);
    if (player.video.getAttribute("src")) {
      player.position = player.video.currentTime;
      player.video.removeAttribute("src"); player.video.load();
    }
  }
  function choose() {
    frame = 0;
    let best = null, score = 0;
    for (const player of players.values()) {
      const ratio = visible(player);
      if (ratio < 0.2 && document.fullscreenElement !== player.shell && !player.video.webkitDisplayingFullscreen) {
        // A hidden page has been left, unlike a card just scrolled out of view.
        // Release its source now so old media cannot compete with the new page.
        if (player.video.closest("[hidden]")) release(player);
        else pause(player);
      }
      if (ratio >= 0.65 && ratio > score && !player.userPaused && !player.blocked && !player.failed
          && (!player.repeatAt || performance.now() >= player.repeatAt)
          && globalThis.louderrSoundPlayback?.canAutoplay(player.video) !== false) {
        best = player; score = ratio;
      }
    }
    if (!canAutoplay() || document.visibilityState === "hidden") return;
    const other = [...document.querySelectorAll(publicMedia)].some(media => !media.paused && !players.has(media));
    if (other) return;
    if (active && !active.video.paused && visible(active) >= 0.65) return;
    if (best && best.video.paused && !best.requested) start(best, false);
  }
  function schedule() {
    if (!frame) frame = requestAnimationFrame(choose);
  }
  function repeatPending(player) {
    return Boolean(player.repeatAt > performance.now() && canAutoplay()
      && !player.userPaused && !player.failed && !player.blocked && visible(player) > 0
      && globalThis.louderrSoundPlayback?.canAutoplay(player.video) !== false);
  }
  function togglePlayback(player) {
    if (!player.video.paused || player.requested || repeatPending(player)) {
      player.userPaused = true; pause(player);
    } else start(player, true);
  }
  function finishPlayback(player) {
    player.requested = false; player.clearWait();
    // Repeat through the same visibility, motion, sound-authority and competing
    // playback checks as initial autoplay. Native loop stalls single-frame MP4s
    // at metadata readiness in Chrome, even when their full range is buffered.
    // Some single-frame clips also end immediately instead of presenting for
    // their declared duration. Hold the last frame rather than replay every RAF.
    const duration = Number.isFinite(player.video.duration) ? player.video.duration : 0;
    const hold = Math.min(1000, Math.max(0, duration * 1000));
    const remaining = Math.max(0, hold - (performance.now() - (player.playStartedAt ?? 0)));
    clearTimeout(player.repeatTimer);
    player.repeatAt = performance.now() + remaining;
    if (remaining > 0) player.repeatTimer = setTimeout(schedule, remaining);
    else schedule();
    player.sync();
  }
  function source(player) {
    if (globalThis.louderrSoundPlayback?.canLoad(player.video) === false) return;
    player.source = globalThis.louderrSoundPlayback?.source(player.video) || player.source;
    if (!player.video.getAttribute("src")) {
      player.video.src = player.source;
      player.video.preload = "metadata";
    }
  }
  async function start(player, manual) {
    if (!visible(player) && !player.video.webkitDisplayingFullscreen) return;
    if (player.requested) return;
    if (manual) {
      if (player.repeatTimer) clearTimeout(player.repeatTimer);
      player.repeatAt = 0;
      if (player.failed || player.blocked) player.message.textContent = "";
      player.userPaused = false; player.blocked = false;
      if (player.failed) {
        player.failed = false;
        source(player); player.video.load();
      }
    }
    if (player.failed || player.blocked) return;
    for (const other of players.values()) if (other !== player) pause(other);
    active = player;
    source(player);
    const generation = ++player.generation;
    player.requested = true;
    player.wait(); player.sync();
    try {
      if (globalThis.louderrSoundPlayback && !await globalThis.louderrSoundPlayback.beforePlay(player.video, manual)) {
        if (generation === player.generation) pause(player);
        return;
      }
      if (generation !== player.generation || !visible(player) && !player.video.webkitDisplayingFullscreen) return;
      source(player);
      await player.video.play();
      if (generation !== player.generation) {
        if (!visible(player) && !player.video.webkitDisplayingFullscreen) pause(player);
        return;
      }
      if (!visible(player) && !player.video.webkitDisplayingFullscreen) pause(player);
    } catch (error) {
      if (generation !== player.generation) return;
      player.requested = false; player.clearWait();
      if (error.name === "NotAllowedError") {
        player.blocked = true; player.message.textContent = "Tap play to watch.";
      } else if (error.name !== "AbortError") {
        player.failed = true; player.message.textContent = "Video couldn't play. Tap to retry.";
      }
      player.sync();
    }
  }
  function updatePictureRatio(video, picture) {
    const valid = (width, height) => Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;
    let width = video.videoWidth, height = video.videoHeight;
    if (!valid(width, height)) {
      width = Number(video.getAttribute("width"));
      height = Number(video.getAttribute("height"));
    }
    if (valid(width, height)) picture.style.setProperty("--video-ratio", `${width} / ${height}`);
  }
  function enhance(video) {
    if (players.has(video) || video.closest(".board-visual")) return;
    const url = globalThis.louderrSoundPlayback?.source(video) || video.getAttribute("src");
    if (!url) return;
    const shell = make("div", "video-player");
    const picture = make("div", "video-picture");
    updatePictureRatio(video, picture);
    const controls = make("div", "video-controls");
    const play = button("Play video", "play"), sound = button("Turn sound on", "muted");
    const fullscreen = button("Expand video", "expand");
    const captions = button("Hide captions", "captions");
    const seek = make("input", "video-seek");
    seek.type = "range"; seek.min = "0"; seek.max = "1000"; seek.step = "1"; seek.value = "0";
    seek.setAttribute("aria-label", "Video position"); seek.disabled = true;
    const clock = make("span", "video-time"); clock.textContent = "0:00 / 0:00";
    const overlay = make("button", "video-toggle"); overlay.type = "button";
    overlay.setAttribute("aria-label", "Play video"); overlay.tabIndex = -1;
    const badge = make("span", "video-state"); badge.setAttribute("aria-hidden", "true");
    overlay.append(badge);
    const message = make("span", "video-message"); message.setAttribute("role", "status");
    message.textContent = globalThis.louderrSoundPlayback?.notice(video) || "";
    const name = video.getAttribute("aria-label");
    if (name) { shell.setAttribute("role", "group"); shell.setAttribute("aria-label", name); }
    video.before(shell); picture.append(video, overlay, message);
    controls.append(play, sound, seek, clock, captions, fullscreen); shell.append(picture, controls);
    const player = { video, shell, source: url, message, generation: 0, position: 0,
      requested: false, userPaused: false, blocked: false, failed: false };
    let spinner = 0, deadline = 0, releaseTimer = 0;
    player.clearWait = () => {
      clearTimeout(spinner); clearTimeout(deadline);
      shell.classList.remove("is-buffering");
    };
    player.wait = () => {
      if (deadline) return;
      spinner = setTimeout(() => { shell.classList.add("is-buffering"); badge.textContent = "Loading…"; }, 700);
      deadline = setTimeout(() => {
        pause(player); player.failed = true;
        message.textContent = navigator.onLine === false ? "You're offline. Tap to retry when connected." : "Video is taking too long. Tap to retry.";
        player.sync();
      }, 15000);
    };
    const clearWait = player.clearWait;
    player.clearWait = () => { clearWait(); spinner = 0; deadline = 0; };
    player.sync = () => {
      const paused = video.paused && !player.requested && !repeatPending(player);
      shell.classList.toggle("is-paused", paused);
      shell.classList.toggle("has-error", player.failed);
      label(play, player.failed ? "Retry video" : paused ? "Play video" : "Pause video", paused ? "play" : "pause");
      overlay.setAttribute("aria-label", play.getAttribute("aria-label"));
      if (!shell.classList.contains("is-buffering")) badge.textContent = player.failed ? "Retry" : paused ? "▶" : "";
      label(sound, video.muted ? "Turn sound on" : "Mute video", video.muted ? "muted" : "sound");
      sound.setAttribute("aria-pressed", String(!video.muted));
      label(fullscreen, document.fullscreenElement === shell ? "Exit full screen" : "Expand video", "expand");
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      seek.disabled = duration <= 0;
      if (document.activeElement !== seek) seek.value = String(duration > 0 ? Math.round(video.currentTime / duration * 1000) : 0);
      seek.setAttribute("aria-valuetext", `${time(video.currentTime)} of ${time(duration)}`);
      clock.textContent = `${time(video.currentTime)} / ${time(duration)}`;
      const track = [...(video.textTracks || [])].find(item => item.kind === "captions" || item.kind === "subtitles");
      if (captions.hidden !== !track) captions.hidden = !track;
      label(captions, track?.mode === "showing" ? "Hide captions" : "Show captions", "captions");
      captions.setAttribute("aria-pressed", String(track?.mode === "showing"));
    };
    const toggle = () => togglePlayback(player);
    play.addEventListener("click", toggle); overlay.addEventListener("click", toggle);
    sound.addEventListener("click", () => {
      video.muted = !video.muted;
      if (video.paused && !player.userPaused) start(player, true);
      player.sync();
    });
    seek.addEventListener("input", () => {
      if (Number.isFinite(video.duration) && video.duration > 0) video.currentTime = Number(seek.value) / 1000 * video.duration;
      player.sync();
    });
    seek.addEventListener("change", () => { if (!video.paused) player.wait(); });
    captions.addEventListener("click", () => {
      for (const track of video.textTracks) if (["captions", "subtitles"].includes(track.kind)) track.mode = track.mode === "showing" ? "hidden" : "showing";
      player.sync();
    });
    fullscreen.hidden = !shell.requestFullscreen && !video.webkitEnterFullscreen;
    fullscreen.addEventListener("click", async () => {
      try {
        if (document.fullscreenElement === shell) await document.exitFullscreen();
        else if (shell.requestFullscreen) await shell.requestFullscreen();
        else { source(player); video.webkitEnterFullscreen(); }
      } catch (_) { message.textContent = "Full screen isn't available here."; }
    });
    video.addEventListener("webkitbeginfullscreen", () => { video.controls = true; });
    video.addEventListener("webkitendfullscreen", () => { video.controls = false; schedule(); });
    video.addEventListener("loadedmetadata", () => {
      updatePictureRatio(video, picture);
      if (player.position && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(player.position, Math.max(0, video.duration - 0.05)); player.position = 0;
      }
      player.sync();
    });
    for (const event of ["timeupdate", "durationchange", "volumechange", "pause", "loadeddata"]) video.addEventListener(event, player.sync);
    video.textTracks?.addEventListener("addtrack", player.sync);
    video.textTracks?.addEventListener("removetrack", player.sync);
    video.textTracks?.addEventListener("change", player.sync);
    video.addEventListener("playing", () => {
      if (!visible(player) && !video.webkitDisplayingFullscreen) { pause(player); return; }
      player.playStartedAt = performance.now();
      player.requested = false; player.failed = false; player.clearWait(); message.textContent = ""; player.sync();
    });
    for (const event of ["waiting", "stalled"]) video.addEventListener(event, () => { if (!video.paused || player.requested) player.wait(); });
    video.addEventListener("error", () => {
      if (!video.getAttribute("src")) return;
      pause(player); player.failed = true; message.textContent = "Video couldn't load. Tap to retry."; player.sync();
    });
    video.addEventListener("ended", () => finishPlayback(player));
    player.near = near => {
      clearTimeout(releaseTimer);
      if (near) { if (canAutoplay() && visible(player) > 0) source(player); }
      else releaseTimer = setTimeout(() => { if (!visible(player) && !video.webkitDisplayingFullscreen && document.fullscreenElement !== shell) release(player); }, 30000);
      schedule();
    };
    player.destroy = () => {
      clearTimeout(releaseTimer); release(player); observer?.unobserve(video); players.delete(video);
      if (active === player) active = null;
    };
    players.set(video, player);
    video.controls = false; video.playsInline = true; video.muted = true; video.loop = false;
    video.preload = "none"; video.removeAttribute("src"); video.load();
    player.sync(); observer?.observe(video);
  }
  const observer = typeof IntersectionObserver === "function" ? new IntersectionObserver(entries => {
    for (const entry of entries) players.get(entry.target)?.near(entry.isIntersecting);
  }, { threshold: [0, 0.2, 0.65, 1] }) : null;
  function scan(root) {
    if (root.matches?.(selector)) enhance(root);
    for (const video of root.querySelectorAll?.(selector) || []) enhance(video);
  }
  const changes = new MutationObserver(records => {
    let changed = false;
    for (const record of records) {
      if (record.type === "attributes") { changed = true; continue; }
      for (const node of record.addedNodes) if (node.nodeType === 1) { scan(node); changed = true; }
      if ([...record.removedNodes].some(node => node.nodeType === 1)) changed = true;
    }
    if (!changed) return;
    for (const player of players.values()) if (!player.video.isConnected) player.destroy();
    schedule();
  });
  document.addEventListener("louderr:carouselchange", event => {
    for (const player of players.values()) {
      if (event.target.contains(player.video) && player.video.closest("[data-gallery-active='false']")) release(player);
    }
    schedule();
  });
  document.addEventListener("play", event => {
    if (!(event.target instanceof HTMLMediaElement)) return;
    for (const media of document.querySelectorAll(publicMedia)) {
      if (media !== event.target && !media.paused) {
        const player = players.get(media);
        if (player) pause(player); else media.pause();
      }
    }
  }, true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      for (const player of players.values()) pause(player);
      for (const media of document.querySelectorAll(publicMedia)) media.pause();
    } else schedule();
  });
  window.addEventListener("pagehide", () => { for (const player of players.values()) release(player); });
  window.addEventListener("pageshow", schedule);
  document.addEventListener("scroll", schedule, { passive: true, capture: true });
  window.addEventListener("resize", schedule, { passive: true });
  document.addEventListener("fullscreenchange", () => {
    for (const player of players.values()) player.sync();
    schedule();
  });
  motion.addEventListener?.("change", () => {
    if (!canAutoplay()) for (const player of players.values()) pause(player);
    schedule();
  });
  navigator.connection?.addEventListener?.("change", () => {
    if (!canAutoplay()) for (const player of players.values()) pause(player);
    schedule();
  });
  changes.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "open"] });
  scan(document); schedule();
  window.louderrVideo = {
    canAutoplay,
    holdSource(video, message) {
      const player = players.get(video);
      if (!player) return false;
      release(player); player.message.textContent = message; player.sync();
      return true;
    },
    selectSource(video, url, options) {
      const player = players.get(video);
      if (!player) return false;
      player.source = url; player.position = options.position;
      // Holds cancel pending play generations. A source change never resumes
      // playback; the next explicit play must pass the authority check again.
      player.userPaused ||= options.manual;
      player.failed = false; player.blocked = false;
      player.message.textContent = options.notice;
      source(player); player.sync(); schedule();
      return true;
    },
  };
})();
